import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {BufferConditionKeys, MeltingTempPreferences, NucleotideType} from 'biojs/BioConstants';
import {PrimerPreferences} from 'constants/PrimerConstants';
import MeltingTempParams from 'molbio/melting-temp/MeltingTempParams';
import {MELTING_TEMP_DEFAULT_ERROR_STATE} from 'molbio/melting-temp/MeltingTempUtils';
import MisprimeParams from 'molbio/misprime/MisprimeParams';
import {
  getMisprimeDefaultsFromPreferences,
  MisprimeOptions,
  setMisprimePreferences,
} from 'molbio/misprime/MisprimeUtils';
import preferences from 'Preferences';
import NumericInput from 'ui-library/inputs/NumericInput';
import Modal from 'ui-library/modals/Modal';
import {DEGREE_SIGN} from 'util/UnicodeSymbols';

interface AssemblyTabSettingsModalProps {
  onRequestClose: () => void;
}

export default function AssemblyTabSettingsModal(props: AssemblyTabSettingsModalProps): JSX.Element {
  const [meltingTempOptions, setMeltingTempOptions] = useState<MeltingTempPreferences>(
    preferences.get(PrimerPreferences.MELTING_TEMP_DNA)
  );
  const [meltingTempOptionsHaveValidationError, setMeltingTempOptionsHaveValidationError] = useState<{
    [key in BufferConditionKeys]: boolean;
  }>(MELTING_TEMP_DEFAULT_ERROR_STATE);
  const [misprimeOptions, setMisprimeOptions] = useState<MisprimeOptions>(
    getMisprimeDefaultsFromPreferences()
  );
  const [secondaryTemp, setSecondaryTemp] = useState<number | null>(
    preferences.get(PrimerPreferences.SECONDARY_TEMP)
  );

  return (
    <Modal
      title="Assembly Primer Settings"
      className="buffer-content-container"
      onRequestSubmit={() => {
        preferences.set({
          [PrimerPreferences.MELTING_TEMP_DNA]: meltingTempOptions,
          [PrimerPreferences.SECONDARY_TEMP]: secondaryTemp,
        });
        setMisprimePreferences(misprimeOptions);
      }}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Save',
          submit: true,
          disabled: Object.values(meltingTempOptionsHaveValidationError).some(
            (optionHasValidationError) => optionHasValidationError
          ),
        },
      ]}
    >
      <h4>Melting Temperature</h4>
      <MeltingTempParams
        nucleotideType={NucleotideType.DNA}
        meltingTempOptions={meltingTempOptions}
        setMeltingTempOptions={setMeltingTempOptions}
        optionsHaveValidationError={meltingTempOptionsHaveValidationError}
        setOptionsHaveValidationError={setMeltingTempOptionsHaveValidationError}
      />
      <h4>Mispriming</h4>
      <MisprimeParams
        misprimeParams={misprimeOptions}
        setMisprimeParams={setMisprimeOptions}
        displayTextPrefix="Find mispriming sites"
      />
      <h4>Self-priming</h4>
      <div className={css(styles.row)}>
        <span>Calculate &Delta;G for secondary structures at </span>
        <NumericInput
          className={css(styles.fixedSizeInput)}
          value={secondaryTemp}
          onChange={(e) => {
            setSecondaryTemp(e.target.numericValue);
          }}
          placeholder="37"
        />
        <span>{`${DEGREE_SIGN}C.`}</span>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    whiteSpace: 'pre-wrap', // Stops Flexbox from stripping trailing spaces from elements
  },
  fixedSizeInput: {
    flex: '0 0 40px',
  },
});
